// ===================================================================
// KAIXO - Pestaña Gastos
// ===================================================================
// CRUD completo de gastos. Muestra totales por categoría del último
// mes y suma total de recurrentes. Permite añadir/editar/borrar.
// ===================================================================

import React, { useEffect, useState, useCallback } from 'react';
import {
    adminExpensesList, adminExpensesCreate,
    adminExpensesUpdate, adminExpensesRemove,
    adminExpensesLiveUsage,
    adminExpensesSummary,
    adminPlatformsRefreshAll
} from '../../../services/adminApi';
import AdminPlatformsSection from './AdminPlatformsSection';
import './AdminExpensesSection.css';

const CATEGORIES = [
    { id: '',         label: 'Todas' },
    { id: 'hosting',  label: 'Hosting' },
    { id: 'ai_api',   label: 'APIs IA' },
    { id: 'domain',   label: 'Dominios' },
    { id: 'license',  label: 'Licencias' },
    { id: 'payroll',  label: 'Sueldos' },
    { id: 'marketing',label: 'Marketing' },
    { id: 'legal',    label: 'Legal' },
    { id: 'tax',      label: 'Impuestos' },
    { id: 'other',    label: 'Otros' }
];

const CAT_LABELS = Object.fromEntries(CATEGORIES.map(c => [c.id, c.label]));

function fmtMoney(c, currency = 'USD') {
    return ((c || 0) / 100).toLocaleString('en-US', { style: 'currency', currency });
}

export default function AdminExpensesSection() {
    const [tab, setTab] = useState('summary');
    return (
        <div className="aex-wrap">
            <div className="aex-subtabs">
                <button className={`aex-subtab ${tab === 'summary' ? 'is-active' : ''}`}
                        onClick={() => setTab('summary')}>📊 Resumen</button>
                <button className={`aex-subtab ${tab === 'platforms' ? 'is-active' : ''}`}
                        onClick={() => setTab('platforms')}>🔌 Plataformas</button>
                <button className={`aex-subtab ${tab === 'list' ? 'is-active' : ''}`}
                        onClick={() => setTab('list')}>📋 Otros gastos</button>
            </div>
            {tab === 'summary' && <SummaryView />}
            {tab === 'platforms' && <AdminPlatformsSection />}
            {tab === 'list' && <ExpensesList />}
        </div>
    );
}

// ─── Resumen unificado ────────────────────────────────────────────
const CAT_HUMAN = {
    hosting: 'Hosting',
    ai_api: 'APIs IA',
    domain: 'Dominios',
    license: 'Licencias',
    payroll: 'Sueldos',
    marketing: 'Marketing',
    legal: 'Legal',
    tax: 'Impuestos',
    other: 'Otros'
};

function fmtRelativeTime(date) {
    if (!date) return 'nunca';
    const ms = Date.now() - new Date(date).getTime();
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `hace ${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `hace ${min}min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `hace ${h}h`;
    return `hace ${Math.floor(h / 24)}d`;
}

function SummaryView() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminExpensesSummary();
            setData(data);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const refreshAll = async () => {
        setRefreshing(true);
        try {
            await adminPlatformsRefreshAll();
            await reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        } finally {
            setRefreshing(false);
        }
    };

    if (loading) return <div className="aex-loading">Cargando resumen…</div>;
    if (error) return <div className="aex-error">{error}</div>;
    if (!data) return null;

    const totalEur = data.totalMonthlyEurCents || 0;
    const infra = data.infrastructure || { items: [], totalEurCents: 0 };
    const manual = data.manual || { byCategory: [], totalEurCents: 0 };

    return (
        <div className="aex-summary">
            <div className="aex-summary-head">
                <div>
                    <div className="aex-summary-label">Coste mensual estimado (EUR)</div>
                    <div className="aex-summary-total">€{(totalEur / 100).toFixed(2)}</div>
                </div>
                <button className="aex-btn-primary" onClick={refreshAll} disabled={refreshing}>
                    {refreshing ? '⟳ Refrescando…' : '↻ Refrescar APIs'}
                </button>
            </div>

            <div className="aex-summary-hint">
                💡 Pulsa <strong>Refrescar APIs</strong> antes de mirar el total para que las plataformas con consumo por API actualicen sus cifras.
                {infra.lastRefreshAt && (
                    <> Última actualización: <strong>{fmtRelativeTime(infra.lastRefreshAt)}</strong>.</>
                )}
            </div>

            {/* Resumen ejecutivo: solo 2 líneas con totales */}
            <div className="aex-summary-block">
                <table className="aex-summary-table">
                    <tbody>
                        <tr>
                            <td className="aex-summary-bucket">
                                <strong>🔌 Plataformas</strong>
                                <div className="aex-summary-bucket-sub">{infra.items.length} proveedor{infra.items.length === 1 ? '' : 'es'} de infraestructura</div>
                            </td>
                            <td className="aex-summary-cost">
                                <strong>€{(infra.totalEurCents / 100).toFixed(2)}</strong>
                            </td>
                        </tr>
                        <tr>
                            <td className="aex-summary-bucket">
                                <strong>📋 Otros gastos</strong>
                                <div className="aex-summary-bucket-sub">
                                    {manual.byCategory.reduce((acc, c) => acc + c.count, 0)} gastos manuales del mes (sueldos, marketing, legal…)
                                </div>
                            </td>
                            <td className="aex-summary-cost">
                                <strong>€{(manual.totalEurCents / 100).toFixed(2)}</strong>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="aex-summary-cta">
                Para ver el detalle, pulsa <strong>🔌 Plataformas</strong> o <strong>📋 Otros gastos</strong> arriba.
            </div>
        </div>
    );
}

function ExpensesList() {
    const [expenses, setExpenses] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('');
    const [editing, setEditing] = useState(null); // null = no modal, 'new' = nuevo, expense object = edit

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminExpensesList(filter ? { category: filter } : {});
            setExpenses(data?.expenses || []);
            setStats(data?.stats || null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { reload(); }, [reload]);

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar este gasto?')) return;
        try {
            await adminExpensesRemove(id);
            reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    return (
        <div className="aex-list-pane">
            <div className="aex-toolbar">
                <select className="aex-select" value={filter} onChange={e => setFilter(e.target.value)}>
                    {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <button className="aex-btn-primary" onClick={() => setEditing('new')}>
                    + Nuevo gasto
                </button>
            </div>

            {stats && (
                <div className="aex-stats">
                    <div>
                        <div className="aex-stat-num">{fmtMoney(stats.monthlyRecurringCents)}</div>
                        <div className="aex-stat-lbl">Recurrente mensual</div>
                    </div>
                    {(stats.last30dByCategory || []).map(c => (
                        <div key={c._id}>
                            <div className="aex-stat-num">{fmtMoney(c.total)}</div>
                            <div className="aex-stat-lbl">{CAT_LABELS[c._id] || c._id} (30d)</div>
                        </div>
                    ))}
                </div>
            )}

            {loading && <div className="ap-loading">Cargando…</div>}
            {error && <div className="ap-error">{error}</div>}

            {!loading && (
                <table className="aex-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Concepto</th>
                            <th>Categoría</th>
                            <th>Proveedor</th>
                            <th>Importe</th>
                            <th>Recurrente</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {expenses.map(e => (
                            <tr key={e._id}>
                                <td>{new Date(e.date).toLocaleDateString()}</td>
                                <td>{e.description}</td>
                                <td>{CAT_LABELS[e.category] || e.category}</td>
                                <td>{e.vendor || '—'}</td>
                                <td>{fmtMoney(e.amountCents, e.currency)}</td>
                                <td>{e.recurring ? '↻ Sí' : '—'}</td>
                                <td>
                                    <button className="aex-btn-mini" onClick={() => setEditing(e)}>✎</button>
                                    <button className="aex-btn-mini-danger" onClick={() => handleDelete(e._id)}>✕</button>
                                </td>
                            </tr>
                        ))}
                        {expenses.length === 0 && (
                            <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                                Sin gastos registrados.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            )}

            {editing !== null && (
                <ExpenseForm
                    expense={editing === 'new' ? null : editing}
                    onCancel={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}
                />
            )}
        </div>
    );
}

function LiveUsageView() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const reload = useCallback(async (force = false) => {
        setLoading(true);
        setError('');
        try {
            const r = await adminExpensesLiveUsage(force);
            setData(r.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(false); }, [reload]);

    if (loading && !data) return <div className="ap-loading">Consultando proveedores…</div>;
    if (error) return <div className="ap-error">{error}</div>;
    if (!data) return null;

    const { providers, month, totals, checkedAt, cached } = data;

    return (
        <div className="aex-live-pane">
            <div className="aex-live-head">
                <p className="aex-live-intro">
                    Día {month.dayOfMonth} de {month.daysInMonth} · {month.monthPct}% del mes transcurrido
                    {cached && <span className="aex-live-cached"> · datos cacheados</span>}
                </p>
                <button className="aex-btn-secondary" onClick={() => reload(true)}>
                    ↻ Forzar actualización
                </button>
            </div>

            <div className="aex-live-totals">
                <div>
                    <div className="aex-stat-num">{fmtMoney(totals.recurringCents)}</div>
                    <div className="aex-stat-lbl">Fijos mensuales</div>
                </div>
                <div>
                    <div className="aex-stat-num">{fmtMoney(totals.variableSpentCents)}</div>
                    <div className="aex-stat-lbl">Variable consumido</div>
                </div>
                <div>
                    <div className="aex-stat-num">{fmtMoney(totals.variableProjectedCents)}</div>
                    <div className="aex-stat-lbl">Variable proyectado</div>
                </div>
                <div className="aex-live-total-final">
                    <div className="aex-stat-num">{fmtMoney(totals.projectedTotalCents)}</div>
                    <div className="aex-stat-lbl">Total proyectado fin de mes</div>
                </div>
            </div>

            <h4 className="aex-live-h4">Proveedores con uso variable</h4>
            <div className="aex-providers-grid">
                {providers.map(p => <ProviderCard key={p.id} p={p} monthPct={month.monthPct} />)}
            </div>

            <p className="aex-live-foot">
                Última comprobación: {new Date(checkedAt).toLocaleString()}.
                Cierre automático a las 00 UTC del día 1 de cada mes.
            </p>
        </div>
    );
}

function ProviderCard({ p, monthPct }) {
    const spent = p.spentCents || 0;
    const projected = p.projectedCents || 0;

    let statusClass = 'aex-prov-ok';
    let statusText = 'OK';
    if (!p.configured) { statusClass = 'aex-prov-skip'; statusText = 'No configurado'; }
    else if (p.error) { statusClass = 'aex-prov-fail'; statusText = 'Fallo'; }
    else if (projected > 5000) { statusClass = 'aex-prov-warn'; statusText = 'Alto'; }

    return (
        <div className={`aex-prov-card ${statusClass}`}>
            <div className="aex-prov-head">
                <strong>{p.label}</strong>
                <span className="aex-prov-status">{statusText}</span>
            </div>
            {!p.configured && (
                <div className="aex-prov-empty">API key no presente</div>
            )}
            {p.configured && p.error && (
                <div className="aex-prov-error" title={p.error}>{p.error}</div>
            )}
            {p.configured && !p.error && (
                <>
                    <div className="aex-prov-spent">{fmtMoney(spent)}</div>
                    <div className="aex-prov-proj">
                        Proyectado: <strong>{fmtMoney(projected)}</strong>
                    </div>
                    <div className="aex-prov-bar">
                        <div className="aex-prov-bar-fill" style={{ width: `${Math.min(100, monthPct)}%` }} />
                    </div>
                    {p.details && (
                        <div className="aex-prov-details">
                            {Object.entries(p.details).map(([k, v]) =>
                                v !== null && v !== undefined && (
                                    <div key={k}><span>{k}:</span> {String(v)}</div>
                                )
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function ExpenseForm({ expense, onCancel, onSaved }) {
    const [form, setForm] = useState(() => expense ? {
        description: expense.description,
        amount: (expense.amountCents / 100).toFixed(2),
        currency: expense.currency,
        category: expense.category,
        vendor: expense.vendor || '',
        date: expense.date ? new Date(expense.date).toISOString().slice(0, 10) : '',
        recurring: !!expense.recurring,
        notes: expense.notes || ''
    } : {
        description: '',
        amount: '',
        currency: 'USD',
        category: 'other',
        vendor: '',
        date: new Date().toISOString().slice(0, 10),
        recurring: false,
        notes: ''
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const submit = async () => {
        setErr('');
        const desc = form.description.trim();
        const amt = parseFloat(form.amount);
        if (!desc || !form.amount) {
            setErr('Concepto e importe son obligatorios');
            return;
        }
        if (!Number.isFinite(amt) || amt < 0) {
            setErr('El importe debe ser un número positivo');
            return;
        }
        if (desc.length > 200) {
            setErr('El concepto no puede pasar de 200 caracteres');
            return;
        }
        const data = {
            description: desc,
            amountCents: Math.round(amt * 100),
            currency: form.currency,
            category: form.category,
            vendor: form.vendor,
            date: form.date,
            recurring: form.recurring,
            notes: form.notes
        };
        setBusy(true);
        try {
            if (expense) await adminExpensesUpdate(expense._id, data);
            else await adminExpensesCreate(data);
            onSaved();
        } catch (e) {
            setErr(e.response?.data?.error || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="aex-modal-overlay" onClick={onCancel}>
            <div className="aex-modal" onClick={e => e.stopPropagation()}>
                <h3>{expense ? 'Editar gasto' : 'Nuevo gasto'}</h3>

                <input className="aex-input" placeholder="Concepto" maxLength={200}
                       value={form.description}
                       onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />

                <div className="aex-row">
                    <input className="aex-input" type="number" step="0.01" min="0" placeholder="Importe"
                           value={form.amount}
                           onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
                    <select className="aex-input" value={form.currency}
                            onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
                        <option>USD</option><option>EUR</option><option>PEN</option><option>GBP</option>
                    </select>
                </div>

                <div className="aex-row">
                    <select className="aex-input" value={form.category}
                            onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                        {CATEGORIES.filter(c => c.id).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                    </select>
                    <input className="aex-input" placeholder="Proveedor (ej: Render)"
                           value={form.vendor}
                           onChange={e => setForm(f => ({ ...f, vendor: e.target.value }))} />
                </div>

                <input className="aex-input" type="date"
                       value={form.date}
                       onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />

                <label className="aex-check">
                    <input type="checkbox" checked={form.recurring}
                           onChange={e => setForm(f => ({ ...f, recurring: e.target.checked }))} />
                    Es un gasto recurrente mensual
                </label>

                <textarea className="aex-input" rows={3} placeholder="Notas (opcional)"
                          value={form.notes}
                          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />

                {err && <div className="aex-error">{err}</div>}

                <div className="aex-modal-actions">
                    <button className="aex-btn-secondary" onClick={onCancel}>Cancelar</button>
                    <button className="aex-btn-primary" onClick={submit} disabled={busy}>
                        {busy ? 'Guardando…' : 'Guardar'}
                    </button>
                </div>
            </div>
        </div>
    );
}
